import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Landmark, RefreshCw, Send, Loader2, Lightbulb, TrendingUp, Wallet, ClipboardList, Users,
  Sparkles, AlertCircle, ArrowRight,
} from "lucide-react";

const ROLE_META = {
  ceo:  { label: "CEO · Strategy",   Icon: Lightbulb,     hue: "from-brand-teal/20 to-brand-teal/5",    accent: "text-brand-teal" },
  cgo:  { label: "CGO · Growth",     Icon: TrendingUp,    hue: "from-emerald-500/20 to-emerald-500/5",  accent: "text-brand-green" },
  cmo:  { label: "CMO · Marketing",  Icon: Sparkles,      hue: "from-pink-500/20 to-pink-500/5",        accent: "text-pink-500" },
  cfo:  { label: "CFO · Finance",    Icon: Wallet,        hue: "from-indigo-500/20 to-indigo-500/5",    accent: "text-indigo-500" },
  coo:  { label: "COO · Operations", Icon: ClipboardList, hue: "from-amber-500/20 to-amber-500/5",      accent: "text-brand-orange" },
  cto:  { label: "CTO · Technology", Icon: Landmark,      hue: "from-sky-500/20 to-sky-500/5",          accent: "text-sky-500" },
  chro: { label: "CHRO · People",    Icon: Users,         hue: "from-fuchsia-500/20 to-fuchsia-500/5",  accent: "text-fuchsia-500" },
  // Legacy aliases (Phase 12.5 responses) — still render if backend returns old keys
  strategy: { label: "CEO · Strategy", Icon: Lightbulb, hue: "from-brand-teal/20 to-brand-teal/5", accent: "text-brand-teal" },
  growth:   { label: "CGO · Growth",   Icon: TrendingUp, hue: "from-emerald-500/20 to-emerald-500/5", accent: "text-brand-green" },
  finance:  { label: "CFO · Finance",  Icon: Wallet, hue: "from-indigo-500/20 to-indigo-500/5", accent: "text-indigo-500" },
  operations: { label: "COO · Operations", Icon: ClipboardList, hue: "from-amber-500/20 to-amber-500/5", accent: "text-brand-orange" },
  people:   { label: "CHRO · People",  Icon: Users, hue: "from-fuchsia-500/20 to-fuchsia-500/5", accent: "text-fuchsia-500" },
};

const PROMPTS = [
  "Should I hire a salesperson?",
  "Should we increase marketing spend?",
  "What's our biggest growth bottleneck?",
  "Why aren't we converting more deals?",
  "What should I stop doing?",
];

function PerspectiveCard({ role, observation, focus, watch, input_needed, updated_at, onProvideContext, onAdopt }) {
  const meta = ROLE_META[role] || ROLE_META.ceo;
  const { Icon, label, hue, accent } = meta;
  const needsInput = input_needed && (input_needed.questions?.length > 0 || input_needed.reason);
  return (
    <div className={`relative overflow-hidden rounded-2xl border ${needsInput ? "border-brand-orange/60" : "border-border"} bg-gradient-to-br ${hue} p-5`} data-testid={`board-perspective-${role}`}>
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background/70 ${accent}`}>
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="font-display text-sm font-semibold uppercase tracking-[0.14em]">{label}</h3>
        {updated_at && <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-brand-green">Updated</span>}
      </div>
      <div className="mt-4 space-y-3 text-sm">
        {observation && <div><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Observation</p><p className="mt-0.5 leading-snug">{observation}</p></div>}
        {focus && <div><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Focus</p><p className="mt-0.5 leading-snug">{focus}</p></div>}
        {watch && <div><p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Watch</p><p className="mt-0.5 leading-snug">{watch}</p></div>}
        {needsInput && (
          <div className="rounded-xl bg-orange-500/10 p-3" data-testid={`board-input-needed-${role}`}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-brand-orange">Input Needed</p>
            <p className="mt-1 text-[12px]">{input_needed.reason}</p>
            {input_needed.questions?.length > 0 && (
              <ul className="mt-1 ml-4 list-disc text-[12px] text-muted-foreground">
                {input_needed.questions.map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            )}
            <button
              type="button"
              onClick={() => onProvideContext && onProvideContext(role, input_needed)}
              data-testid={`board-provide-context-${role}`}
              className="mt-2 inline-flex items-center gap-1 rounded-full bg-brand-orange px-3 py-1 text-[11px] font-semibold text-white"
            >
              Provide context
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ChiefOfStaffCard({ recommendation, reasoning }) {
  if (!recommendation) return null;
  return (
    <div className="rounded-2xl border border-brand-teal/40 bg-brand-teal/5 p-5" data-testid="board-chief-of-staff">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-teal text-white">
          <Sparkles className="h-4 w-4" />
        </span>
        <h3 className="font-display text-sm font-semibold uppercase tracking-[0.14em]">Chief of Staff · Recommendation</h3>
      </div>
      <p className="mt-3 text-[15px] font-medium leading-snug">{recommendation}</p>
      {reasoning && <p className="mt-2 text-[12px] italic text-muted-foreground">{reasoning}</p>}
    </div>
  );
}

export default function Board() {
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [conversation, setConversation] = useState([]); // [{role:"user"|"board", payload:...}]
  const [convId, setConvId] = useState(null);
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState("");
  const [adoptedDecision, setAdoptedDecision] = useState(null);

  const loadOverview = useCallback(async (mode = "load") => {
    if (mode === "refresh") setRefreshing(true); else setLoadingOverview(true);
    try {
      const url = mode === "refresh" ? "/board/refresh" : "/board/overview";
      const { data } = mode === "refresh" ? await api.post(url) : await api.get(url);
      setOverview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "The Board couldn't convene right now.");
    } finally {
      if (mode === "refresh") setRefreshing(false); else setLoadingOverview(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const ask = async (msg) => {
    const q = (msg ?? question).trim();
    if (!q || asking) return;
    setAsking(true);
    setConversation((c) => [...c, { role: "user", text: q }]);
    setQuestion("");
    try {
      const { data } = await api.post("/board/ask", { message: q, conversation_id: convId });
      if (data.conversation_id && !convId) setConvId(data.conversation_id);
      setConversation((c) => [...c, { role: "board", payload: data }]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "The Board couldn't answer.");
      setConversation((c) => [...c, { role: "board", error: true }]);
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6" data-testid="board-page">
      <Seo title="Board — Leadership Team" description="Strategic perspectives grounded in what's actually happening across your business." path="/app/board" />

      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-brand-teal">
            <Landmark className="h-3 w-3" /> Board
          </span>
          <h1 className="mt-2 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">Your Leadership Team</h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            Strategic perspectives grounded in what's actually happening across your business. This is where you <em>think</em> about the business — not just run it.
          </p>
        </div>
        <button
          onClick={() => loadOverview("refresh")}
          disabled={refreshing || loadingOverview}
          data-testid="board-refresh-btn"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-60"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh Board
        </button>
      </header>

      {/* Board Brief */}
      <section className="rounded-3xl border border-border bg-card p-5 sm:p-6" data-testid="board-brief">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Board Brief</p>
        {loadingOverview ? (
          <p className="mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> The Board is convening…</p>
        ) : overview?.board_brief ? (
          <p className="mt-2 text-[15px] leading-relaxed">{overview.board_brief}</p>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">Not enough workspace data yet. Add a primary goal, active opportunities, and current projects — then refresh.</p>
        )}
        {overview?.data_sufficient === false && (
          <div className="mt-3 inline-flex items-start gap-2 rounded-xl bg-orange-500/10 px-3 py-2 text-[12px] text-brand-orange" data-testid="board-data-gap">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Board perspectives will sharpen as you add more workspace data.
          </div>
        )}
      </section>

      {/* Perspectives grid */}
      {overview?.perspectives && (
        <section className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="board-perspectives-grid">
          {["ceo", "cgo", "cmo", "cfo", "coo", "cto", "chro", "strategy", "growth", "finance", "operations", "people"].map((r) => {
            const p = overview.perspectives[r];
            if (!p) return null;
            return (
              <PerspectiveCard
                key={r}
                role={r}
                {...p}
                onProvideContext={(role, input) => {
                  const qs = (input.questions || []).join(" ");
                  setQuestion(`[${role.toUpperCase()} context] ${qs}`.slice(0, 400));
                  document.querySelector('[data-testid="board-ask-input"]')?.focus();
                }}
              />
            );
          })}
        </section>
      )}

      {overview?.stale && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-orange-500/40 bg-orange-500/10 p-3 text-[12px]" data-testid="board-stale-banner">
          <AlertCircle className="h-4 w-4 text-brand-orange" />
          <span>New developments since this Board assessment.</span>
          <button onClick={() => loadOverview("refresh")} className="ml-auto rounded-full bg-brand-orange px-3 py-1 text-[11px] font-semibold text-white">Refresh Board</button>
        </div>
      )}

      {overview?.chief_of_staff && (
        <div className="mt-5">
          <ChiefOfStaffCard {...overview.chief_of_staff} />
          <button
            type="button"
            data-testid="board-adopt-decision-btn"
            onClick={async () => {
              try {
                const { data } = await api.post("/board/decisions", {
                  title: (overview.chief_of_staff.recommendation || "Board decision").slice(0, 120),
                  decision: overview.chief_of_staff.recommendation,
                  rationale: overview.chief_of_staff.reasoning || "",
                });
                toast.success("Board decision adopted.");
                setAdoptedDecision(data.decision);
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not adopt.");
              }
            }}
            className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-brand-teal/40 bg-brand-teal/10 px-3 py-1.5 text-[11px] font-semibold text-brand-teal hover:bg-brand-teal/20"
          >
            Adopt as Board Decision <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Ask the Board */}
      <section className="mt-8 rounded-3xl border border-border bg-card p-5 sm:p-6" data-testid="board-ask">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Ask the Board</p>
        <p className="mt-1 text-sm text-muted-foreground">Get leadership perspectives on a specific decision. Follow-ups keep context.</p>

        {conversation.length === 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {PROMPTS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => ask(p)}
                disabled={asking}
                data-testid={`board-prompt-${p.split(" ")[0].toLowerCase()}`}
                className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:border-brand-teal/60 disabled:opacity-60"
              >
                {p}
              </button>
            ))}
          </div>
        )}

        <div className="mt-4 space-y-4" data-testid="board-conversation">
          {conversation.map((entry, i) => (
            entry.role === "user" ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl brand-gradient-bg px-4 py-2.5 text-sm text-white">
                  {entry.text}
                </div>
              </div>
            ) : entry.error ? (
              <div key={i} className="rounded-xl border border-red-500/30 bg-red-500/5 p-3 text-xs text-brand-red">The Board couldn't reach a decision. Please try again.</div>
            ) : (
              <div key={i} className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {(entry.payload.perspectives || []).map((p) => (
                    <PerspectiveCard key={`${i}-${p.role}`} role={p.role} observation={p.observation} focus={p.focus} watch={p.watch} />
                  ))}
                </div>
                {entry.payload.chief_of_staff && <ChiefOfStaffCard {...entry.payload.chief_of_staff} />}
                {entry.payload.data_gap && (
                  <div className="inline-flex items-start gap-2 rounded-xl bg-orange-500/10 px-3 py-2 text-[12px] text-brand-orange">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    Missing context to strengthen this answer: {entry.payload.data_gap}
                  </div>
                )}
              </div>
            )
          ))}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); ask(); }}
          className="mt-5 flex items-end gap-2"
        >
          <div className="flex-1">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
              placeholder="Ask your leadership team…"
              rows={2}
              disabled={asking}
              data-testid="board-ask-input"
              className="w-full resize-none rounded-2xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary disabled:opacity-60"
            />
          </div>
          <button
            type="submit"
            disabled={asking || !question.trim()}
            data-testid="board-ask-submit"
            className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            {asking ? <><Loader2 className="h-4 w-4 animate-spin" /> Deliberating…</> : <>Ask <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>
        {conversation.length > 0 && !asking && (
          <button
            type="button"
            onClick={() => { setConversation([]); setConvId(null); }}
            data-testid="board-new-topic"
            className="mt-3 text-[11px] font-semibold text-brand-teal hover:underline"
          >
            Start a new topic →
          </button>
        )}
      </section>
    </div>
  );
}
